/**
 * Comparators for the list detail views.
 *
 * Sorting applies within groups when a group-by is active; group order is unaffected.
 */
import type { EnrichedListCard, ListPriority } from '@/types/lists';

import type { ListSortState } from '../state/listViewSettings';

/** High first — the order a want-list is naturally read in. */
const PRIORITY_ORDER: Record<ListPriority, number> = { high: 0, medium: 1, low: 2 };

export interface ListSortAccessors {
  /** Display order of the card's type category; rows with no cached card sort last. */
  typeSortOrder: (card: EnrichedListCard) => number;
}

/** Rows with no price sort last in both directions, so `—` never leads the table. */
function comparePrice(a: EnrichedListCard, b: EnrichedListCard, direction: 1 | -1): number {
  if (a.unit_price == null && b.unit_price == null) return 0;
  if (a.unit_price == null) return 1;
  if (b.unit_price == null) return -1;
  return (a.unit_price - b.unit_price) * direction;
}

export function createListCardComparator(
  sort: ListSortState,
  accessors: ListSortAccessors,
): (a: EnrichedListCard, b: EnrichedListCard) => number {
  const direction = sort.direction === 'asc' ? 1 : -1;

  return (a, b) => {
    let result = 0;

    switch (sort.field) {
      case 'type':
        result = (accessors.typeSortOrder(a) - accessors.typeSortOrder(b)) * direction;
        break;
      case 'priority':
        result = (PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]) * direction;
        break;
      case 'quantity':
        result = (a.quantity - b.quantity) * direction;
        break;
      case 'owned':
        result = (a.owned_quantity - b.owned_quantity) * direction;
        break;
      case 'available':
        result = (a.available_quantity - b.available_quantity) * direction;
        break;
      case 'price':
        result = comparePrice(a, b, direction);
        break;
      case 'name':
        result = a.name.localeCompare(b.name) * direction;
        break;
    }

    // Name is the stable tiebreaker so equal keys never reorder between renders.
    return result !== 0 ? result : a.name.localeCompare(b.name);
  };
}
